import { DueDateRuleEnum } from '../types/catering/cateringPaymentPlan.types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export interface InstallmentDueDateRuleInput {
  dueDateRule?: DueDateRuleEnum | string | null;
  days?: number | null;
}

const parseWithTimezoneAndFormats = (value: string, formats: string[], restaurantTz: string): dayjs.Dayjs | null => {
  for (const format of formats) {
    const parsed = dayjs.tz(value, format, restaurantTz);
    if (parsed.isValid() && parsed.format(format) === value) {
      return parsed;
    }
  }

  return null;
};

const parseEventDateTime = (
  eventDate: string | Date,
  eventTime?: string,
  restaurantTz?: string
): dayjs.Dayjs | null => {
  const hasRestaurantTz = typeof restaurantTz === 'string' && restaurantTz.trim().length > 0;

  if (eventDate instanceof Date) {
    const parsedDate = hasRestaurantTz ? dayjs(eventDate).tz(restaurantTz) : dayjs(eventDate);
    if (!parsedDate.isValid()) return null;

    if (!eventTime) return parsedDate;
    const [hourStr, minuteStr] = eventTime.split(':');
    const hour = Number(hourStr);
    const minute = Number(minuteStr);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return parsedDate;
    return parsedDate.hour(hour).minute(minute).second(0).millisecond(0);
  }

  if (typeof eventDate !== 'string' || !eventDate.trim()) {
    return null;
  }

  if (eventTime) {
    const strictDateTime = hasRestaurantTz
      ? parseWithTimezoneAndFormats(
          `${eventDate} ${eventTime}`,
          ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD HH:mm:ss'],
          restaurantTz
        )
      : dayjs(`${eventDate} ${eventTime}`, ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD HH:mm:ss'], true);
    if (strictDateTime && strictDateTime.isValid()) {
      return strictDateTime;
    }
  }

  const strictYyyyMmDd = hasRestaurantTz
    ? parseWithTimezoneAndFormats(eventDate, ['YYYY-MM-DD'], restaurantTz)
    : dayjs(eventDate, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm:ss.SSSZ'], true);
  if (strictYyyyMmDd && strictYyyyMmDd.isValid()) {
    return strictYyyyMmDd;
  }

  if (hasRestaurantTz) {
    const parsedWithTimezone = dayjs(eventDate).tz(restaurantTz);
    if (parsedWithTimezone.isValid()) {
      return parsedWithTimezone;
    }
  }

  const fallbackParsedDate = dayjs(eventDate);
  return fallbackParsedDate.isValid() ? fallbackParsedDate : null;
};

export const getInstallmentDueDateForEventDate = (
  installment: InstallmentDueDateRuleInput,
  eventDate: string | Date,
  eventTime?: string,
  referenceDate: Date = new Date(),
  restaurantTz?: string
): Date | null => {
  const normalizedEventDate = parseEventDateTime(eventDate, eventTime, restaurantTz);
  if (!normalizedEventDate) {
    return null;
  }

  const dueDate = normalizedEventDate;
  const normalizedReferenceDate =
    restaurantTz && restaurantTz.trim().length > 0 ? dayjs(referenceDate).tz(restaurantTz) : dayjs(referenceDate);

  switch (installment.dueDateRule) {
    case DueDateRuleEnum.ON_ORDER_ACCEPTANCE:
      return normalizedReferenceDate.toDate();
    case DueDateRuleEnum.ON_EVENT_DAY:
      return dueDate.toDate();
    case DueDateRuleEnum.DAYS_BEFORE_EVENT:
      return dueDate.subtract(installment.days ?? 0, 'day').toDate();
    case DueDateRuleEnum.DAYS_AFTER_EVENT:
      return dueDate.add(installment.days ?? 0, 'day').toDate();
    default:
      return null;
  }
};

export const hasPastInstallmentsForEventDate = (
  installments: InstallmentDueDateRuleInput[],
  eventDate: string | Date,
  eventTime?: string,
  referenceDate: Date = new Date(),
  restaurantTz?: string
): boolean => {
  const now =
    restaurantTz && restaurantTz.trim().length > 0 ? dayjs(referenceDate).tz(restaurantTz) : dayjs(referenceDate);
  const pastThreshold = now.subtract(1, 'minute');

  return installments.some((installment) => {
    const dueDate = getInstallmentDueDateForEventDate(installment, eventDate, eventTime, referenceDate, restaurantTz);
    const normalizedDueDate =
      dueDate && restaurantTz && restaurantTz.trim().length > 0 ? dayjs(dueDate).tz(restaurantTz) : dayjs(dueDate);

    return !!dueDate && normalizedDueDate.isBefore(pastThreshold);
  });
};

export const isPaymentPlanValidForEventDate = (
  installments: InstallmentDueDateRuleInput[],
  eventDate?: string | Date,
  eventTime?: string,
  referenceDate: Date = new Date(),
  restaurantTz?: string
): boolean => {
  if (!eventDate) {
    return true;
  }

  return !hasPastInstallmentsForEventDate(installments, eventDate, eventTime, referenceDate, restaurantTz);
};
